// src/index.ts
import * as Sentry4 from "@sentry/react";

// src/init.ts
import * as Sentry from "@sentry/react";

// src/sanitize.ts
var SENSITIVE_KEYS = [
  "password",
  "passwd",
  "secret",
  "api_key",
  "apikey",
  "authorization",
  "private_key",
  "privatekey",
  "access_token",
  "refresh_token",
  "cvv",
  "cvc",
  "pin"
];
var PARTIAL_REDACT_KEYS = [
  "account_number",
  "accountnumber",
  "routing_number",
  "routingnumber",
  "card_number",
  "cardnumber",
  "credit_card",
  "ssn",
  "social_security"
];
var SSN_REGEX = /\b\d{3}[-\s]?\d{2}[-\s]?\d{4}\b/g;
var CREDIT_CARD_REGEX = /\b(?:\d{4}[-\s]?){3}\d{4}\b/g;
function isSensitiveKey(key) {
  const lowerKey = key.toLowerCase();
  return SENSITIVE_KEYS.some((sensitive) => lowerKey.includes(sensitive));
}
function isPartialRedactKey(key) {
  const lowerKey = key.toLowerCase();
  return PARTIAL_REDACT_KEYS.some((partial) => lowerKey.includes(partial));
}
function partialRedact(value) {
  if (value.length <= 4) {
    return "[REDACTED]";
  }
  const lastFour = value.slice(-4);
  return `[REDACTED ***${lastFour}]`;
}
function sanitizeString(value) {
  return value.replace(SSN_REGEX, "[SSN REDACTED]").replace(CREDIT_CARD_REGEX, "[CARD REDACTED]");
}
function sanitizeValue(key, value) {
  if (isSensitiveKey(key)) {
    return "[REDACTED]";
  }
  if (isPartialRedactKey(key) && typeof value === "string") {
    return partialRedact(value);
  }
  if (typeof value === "string") {
    return sanitizeString(value);
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => sanitizeValue(String(index), item));
  }
  if (value && typeof value === "object") {
    return sanitizeObject(value);
  }
  return value;
}
function sanitizeObject(obj) {
  const sanitized = {};
  for (const [key, value] of Object.entries(obj)) {
    sanitized[key] = sanitizeValue(key, value);
  }
  return sanitized;
}
function applyFingerprinting(event, hint) {
  const error = hint?.originalException;
  if (error instanceof Error) {
    if (error.name === "ValidationError" || error.name === "ZodError") {
      event.fingerprint = ["validation-error", "{{ default }}"];
      return event;
    }
    if (error.message?.includes("fetch") || error.message?.includes("network")) {
      event.fingerprint = ["network-error", "{{ default }}"];
      return event;
    }
  }
  return event;
}
function sanitizeEvent(event, hint) {
  if (event.user) {
    if (event.user.ip_address) {
      event.user.ip_address = "[IP REDACTED]";
    }
  }
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
      if (breadcrumb.data) {
        breadcrumb.data = sanitizeObject(breadcrumb.data);
      }
      if (breadcrumb.message) {
        breadcrumb.message = sanitizeString(breadcrumb.message);
      }
      return breadcrumb;
    });
  }
  if (event.extra) {
    event.extra = sanitizeObject(event.extra);
  }
  if (event.tags) {
    event.tags = sanitizeObject(event.tags);
  }
  return applyFingerprinting(event, hint);
}

// src/init.ts
var isInitialized = false;
function initSentryBrowser(options) {
  if (isInitialized) {
    console.warn("[Sentry] Already initialized, skipping...");
    return;
  }
  if (!options.dsn) {
    console.warn("[Sentry] No DSN provided, Sentry will not be initialized");
    return;
  }
  Sentry.init({
    dsn: options.dsn,
    environment: options.environment ?? "development",
    release: options.release,
    debug: options.debug ?? false,
    integrations: [
      Sentry.browserTracingIntegration(),
      ...options.enableReplay !== false ? [
        Sentry.replayIntegration({
          maskAllText: false,
          maskAllInputs: false,
          blockAllMedia: false,
          // Only mask truly sensitive inputs - keep everything else visible for debugging
          mask: [
            'input[type="password"]',
            'input[name*="cvv"]',
            'input[name*="cvc"]',
            'input[name*="ssn"]',
            'input[name*="social_security"]',
            // Mask full account/routing numbers (but NOT bank names)
            'input[name="account_number"]',
            'input[name="accountNumber"]',
            'input[name="routing_number"]',
            'input[name="routingNumber"]'
          ],
          // Block nothing - we want to see everything except sensitive inputs
          block: []
        })
      ] : []
    ],
    // Performance monitoring
    tracesSampleRate: options.tracesSampleRate ?? 0.1,
    // Session Replay sampling
    replaysSessionSampleRate: options.replaysSessionSampleRate ?? 0.1,
    replaysOnErrorSampleRate: options.replaysOnErrorSampleRate ?? 1,
    // PII sanitization
    beforeSend: sanitizeEvent,
    // Error filtering
    ignoreErrors: [
      // Browser extension errors
      "ResizeObserver loop",
      "ResizeObserver loop limit exceeded",
      // Network errors
      "Failed to fetch",
      "NetworkError",
      "Load failed",
      // User-triggered
      "AbortError"
    ],
    // URL filtering
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i
    ]
  });
  Sentry.setTag("app", options.appName);
  isInitialized = true;
  console.log(`[Sentry] Initialized for ${options.appName}`);
}
function isSentryInitialized() {
  return isInitialized;
}

// src/init-embed.ts
import * as Sentry2 from "@sentry/react";
var isInitialized2 = false;
function initSentryEmbed(options) {
  if (isInitialized2) {
    return;
  }
  if (!options.dsn) {
    return;
  }
  Sentry2.init({
    dsn: options.dsn,
    environment: options.environment ?? "production",
    release: options.release,
    debug: options.debug ?? false,
    // No integrations for minimal footprint
    integrations: [],
    // Disable performance monitoring for embed
    tracesSampleRate: 0,
    // Disable Session Replay for embed
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    // PII sanitization
    beforeSend: sanitizeEvent,
    // Error filtering
    ignoreErrors: [
      "ResizeObserver loop",
      "ResizeObserver loop limit exceeded",
      "Failed to fetch",
      "NetworkError",
      "Load failed",
      "AbortError",
      // Shadow DOM specific
      "Cannot read property",
      "undefined is not an object"
    ],
    // URL filtering
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i
    ]
  });
  Sentry2.setTag("app", "flote-embed");
  Sentry2.setTag("host_domain", options.hostDomain);
  isInitialized2 = true;
}
function isSentryEmbedInitialized() {
  return isInitialized2;
}

// src/error-boundary.tsx
import * as Sentry3 from "@sentry/react";
import { jsx, jsxs } from "react/jsx-runtime";
function SentryErrorBoundary({
  children,
  fallback,
  onError,
  showDialog = false
}) {
  return /* @__PURE__ */ jsx(
    Sentry3.ErrorBoundary,
    {
      fallback: fallback ?? /* @__PURE__ */ jsxs(
        "div",
        {
          style: {
            padding: "20px",
            textAlign: "center",
            fontFamily: "system-ui, sans-serif"
          },
          children: [
            /* @__PURE__ */ jsx("h2", { style: { color: "#dc2626", marginBottom: "10px" }, children: "Something went wrong" }),
            /* @__PURE__ */ jsx("p", { style: { color: "#6b7280" }, children: "We've been notified and are working on a fix." })
          ]
        }
      ),
      showDialog,
      onError: (error, componentStack, eventId) => {
        Sentry3.withScope((scope) => {
          scope.setExtra("componentStack", componentStack);
          scope.setExtra("eventId", eventId);
        });
        if (onError) {
          onError(error, { componentStack });
        }
      },
      children
    }
  );
}
function withSentryErrorBoundary(Component, options = {}) {
  const WrappedComponent = (props) => /* @__PURE__ */ jsx(SentryErrorBoundary, { ...options, children: /* @__PURE__ */ jsx(Component, { ...props }) });
  WrappedComponent.displayName = `withSentryErrorBoundary(${Component.displayName ?? Component.name ?? "Component"})`;
  return WrappedComponent;
}
export {
  Sentry4 as Sentry,
  SentryErrorBoundary,
  initSentryBrowser,
  initSentryEmbed,
  isSentryEmbedInitialized,
  isSentryInitialized,
  sanitizeEvent,
  withSentryErrorBoundary
};
